import React, { useEffect, useState, useContext } from "react";
import { ActionFunctionArgs, useLoaderData, Form, useNavigate, useSearchParams } from "react-router-dom";
import { Service } from "../service";
import { LoaderData, SitePayload } from "../../types";
import { LoaderFunctionArgs, redirect } from "react-router";
import {
    Alert,
    AppBar,
    Button,
    Card,
    Checkbox,
    Chip,
    ChipProps,
    FormControlLabel,
    FormGroup,
    Modal,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    FormHelperText
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from "@mui/material/Tooltip";
import { authenticatedRequest } from "../authenticate/authenticatedRequest";
import { Description } from "@mui/icons-material";
import AuthenticatedAppContext from "../authenticate/AuthenticatedAppContext";
import { DynamicModal } from "../modal";
import { ToastMessage } from "../toast";
import {
    parseQueueId,
    pipelineInProgressLabel,
    watchSitePipeline,
    SitePipelineStatus,
    interpretPipelineStatus,
    pipelineOutcomeToast,
    siteRecordExists,
} from "./pipelineStatus";
import { hasRequiredCanonicalHostname } from "./canonicalHostname";
import {
    cloneSiteIdHelperText,
    isCloneSiteIdValid,
    isSiteIdWithinLimit,
    MAX_SITE_ID_LENGTH,
    SITE_ID_TOO_LONG_MESSAGE,
    siteIdCharacterCount,
} from "./siteId";
import { DEFAULT_SITE_THEME, SITE_THEME_OPTIONS, normalizeSiteTheme } from "../../constants/siteThemes";

const PLATFORM_INGRESS_PATTERN = process.env.REACT_APP_PLATFORM_INGRESS_PATTERN || "rezfusion.com";

export interface SiteProps {
    name: string;
    hostnames: string[];
    id: string;
    service: "blue" | "green"
    canonicalHostname: string;
    enableWww: boolean;
    brandSlug: string;
    isProd: boolean;
    theme: string;
    createdDate: string | null;
    goLiveDate: string | null;
}

export async function site({ params }: LoaderFunctionArgs): Promise<SiteProps> {
    if (!params.id) {
        return {
            name: "",
            id: "",
            service: "blue",
            canonicalHostname: "",
            enableWww: false,
            hostnames: [],
            brandSlug: "bluetent",
            isProd: false,
            theme: DEFAULT_SITE_THEME,
            createdDate: null,
            goLiveDate: null,
        }
    }

    const data = await authenticatedRequest(`${process.env.REACT_APP_PROXY_SERVER}/api/sites/${params.id}`)
        .then(r => r.json());
    const now = new Date().toISOString();

    return {
        name: data.name || "",
        id: data.id || params.id,
        service: data.service || "",
        canonicalHostname: data.canonicalHostname || `${params.id}.${PLATFORM_INGRESS_PATTERN}`,
        enableWww: data.enableWww,
        hostnames: data.hostnames || [`${params.id}.${PLATFORM_INGRESS_PATTERN}`],
        brandSlug: data.brandSlug || "",
        isProd: data.isProd,
        theme: normalizeSiteTheme(data.theme),
        createdDate: data.createdDate || now,
        goLiveDate: data.goLiveDate || null,
    }
}

const Loader = () => (<CircularProgress color="primary"></CircularProgress>);

export const SiteLoader = (): React.JSX.Element => {
    const props = useLoaderData() as LoaderData<typeof site>;
    return (
        <React.Suspense fallback={<Loader />}>
            <Site name={props.name}
                hostnames={props.hostnames}
                id={props.id}
                service={props.service}
                canonicalHostname={props.canonicalHostname}
                enableWww={props.enableWww}
                brandSlug={props.brandSlug}
                isProd={props.isProd}
                theme={props.theme}
                createdDate={props.createdDate}
                goLiveDate={props.goLiveDate}
            />
        </React.Suspense>
    )
}

export const SiteConfigPage = (): React.JSX.Element => {
    const props = useLoaderData() as LoaderData<typeof site>;
    return (
        <Card sx={{ mx: "auto", p: 1, maxWidth: "950px", width: "100%" }}>
            <SiteForm isNew={props.id === ""} />
        </Card>
    )
}

export async function createSite({ params, request }: ActionFunctionArgs): Promise<{ ok: boolean, reason?: string }> {
    const form = document.getElementById("site-create-form") as HTMLFormElement;
    const formData = await request.formData();
    const siteId = String(formData.get("id") ?? "");
    if (!isSiteIdWithinLimit(siteId)) {
        alert(SITE_ID_TOO_LONG_MESSAGE);
        return { ok: false, reason: 'site ID too long' };
    }
    if (!form.checkValidity()) {
        return { ok: false, reason: 'bad site ID' };
    }
    // eslint-disable-next-line no-restricted-globals
    const conf = confirm(
        `You are about to create & provision a new website with the following ID: ${formData.get("id")}. 
        Are you sure you want to proceed? This action creates a database, S3 file system, and a repository entry for the entered website.`
    );
    if (!conf) {
        return { ok: false, reason: 'cancelled' }
    }
    const now = new Date().toISOString();
    const payload: SitePayload = {
        name: formData.get("name"),
        id: formData.get("id"),
        service: formData.get("service"),
        enableWww: formData.get("enableWww") === 'on',
        hostnames: formData.getAll('hostnames'),
        canonicalHostname: formData.get("canonicalHostname"),
        brandSlug: formData.get('brandSlug'),
        isProd: formData.get('isProd') === 'on',
        theme: normalizeSiteTheme(String(formData.get('theme') || '')),
        createdDate: now,
        goLiveDate: null,
    };
    if (!payload.brandSlug && (!payload.hostnames || payload.hostnames.length <= 0)) {
        alert('At least one valid hostname must be present.');
        return { ok: false, reason: 'Empty hostname value.' }
    }

    if (!hasRequiredCanonicalHostname(payload.canonicalHostname)) {
        alert('Canonical hostname is required. Select a hostname from the list (enter a Site ID first for new sites).');
        return { ok: false, reason: 'Empty canonical hostname value.' }
    }

    // First, create the repository entry for the new site.
    const repoCreateRes = await authenticatedRequest(
        `${process.env.REACT_APP_PROXY_SERVER}/api/sites/create`,
        {
            method: "post",
            body: JSON.stringify(payload),
        }
    );
    if (!repoCreateRes.ok) {
        alert(await repoCreateRes.text());
        throw repoCreateRes;
    }

    // If it succeeded, then move on to provisioning the website. This will initialize the DB and codebase as well as the
    // administrative and DB-related secrets. Additionally, an S3 bucket keyed by the site ID is created.
    const provisionSiteRes = await authenticatedRequest(
        `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${payload.id}/provision`,
        {
            method: "put",
            body: JSON.stringify({}),
        }
    );
    if (!provisionSiteRes.ok) {
        // Backend rolls back on queue failure; still attempt destroy if create succeeded and provision failed otherwise.
        console.error(provisionSiteRes);
        alert(await provisionSiteRes.text());
        throw provisionSiteRes
    };

    const provisionBody = await provisionSiteRes.json();
    const qid = parseQueueId(provisionBody);
    if (qid) {
        return redirect(`/sites/${payload.id}?pipeline=${encodeURIComponent(qid)}&pipelineKind=provision`);
    }
    return redirect(`/sites/${payload.id}`);
}

export async function updateSite({ params, request }: ActionFunctionArgs): Promise<{ ok: boolean, reason?: string }> {
    const formData = await request.formData();
    const now = new Date().toISOString();
    // If a site is set to production and no go live date is set, set it to the current date.
    if (formData.get('isProd') === 'on' && (!formData.get('goLiveDate') || formData.get('goLiveDate') === '')) {
        formData.set('goLiveDate', now);
    } else if (formData.get('isProd') === 'off') {
        formData.set('goLiveDate', '');
    }
    const payload: SitePayload = {
        name: formData.get("name"),
        id: params.id,
        service: formData.get("service"),
        enableWww: formData.get("enableWww") === 'on',
        hostnames: formData.getAll('hostnames'),
        canonicalHostname: formData.get("canonicalHostname"),
        brandSlug: formData.get('brandSlug'),
        isProd: formData.get('isProd') === 'on',
        theme: normalizeSiteTheme(String(formData.get('theme') || '')),
        createdDate: formData.get('createdDate'),
        goLiveDate: formData.get('goLiveDate'),
    };

    if (!hasRequiredCanonicalHostname(payload.canonicalHostname)) {
        alert('Canonical hostname is required. Select a hostname from the eligible hostnames list.');
        return { ok: false, reason: 'Empty canonical hostname value.' }
    }
    if (!payload.brandSlug && (!payload.hostnames || payload.hostnames.length <= 0)) {
        alert('At least one valid hostname must be present.');
        return { ok: false, reason: 'Empty hostname value.' }
    }

    // First, update the repository entry for this site.
    const repoUpdateRes = await authenticatedRequest(
        `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${params.id}`,
        {
            method: "post",
            body: JSON.stringify(payload),
        }
    );
    if (!repoUpdateRes.ok) throw repoUpdateRes;
    // Then, fire off
    const siteUpgradeRes = await authenticatedRequest(
        `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${params.id}/upgrade`,
        {
            method: "put",
            body: JSON.stringify({}),
        }
    );
    if (!siteUpgradeRes.ok) throw siteUpgradeRes;
    return redirect('/sites');
}

type AddHostnameModalProps = {
    setHostnames: (hostnames: string) => void,
    open: boolean,
    handleClose: (open: boolean) => void
};

function isValidDomain(domain: string) {
    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/;
    return domainRegex.test(domain);
}

export const AddHostnameModal = ({ open, handleClose, setHostnames }: AddHostnameModalProps): React.JSX.Element => {
    const [newHostname, setNewHostname] = useState<string>("");
    const [hostnameValid, setHostnameValid] = useState<boolean>(false);
    const style = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
    };
    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={{ ...style }}>
                <Grid container>
                    <Grid container>
                        <Grid item sm={12}>
                            <AppBar position="relative">
                                <Box display="flex" justifyContent="end" alignContent="center">
                                    <Typography
                                        py={1.5}
                                        px={2}
                                        component="h1"
                                        variant="h6"
                                        color="inherit"
                                        noWrap
                                        sx={{ flexGrow: 1 }}
                                    >
                                        Add Hostname
                                    </Typography>
                                    <Box py={1}
                                        px={2}
                                        pr={2}>
                                        <IconButton
                                            aria-label="exit adding new hostnames"
                                            onClick={() => {
                                                handleClose(false);
                                            }}
                                            edge="end"
                                        >
                                            <CloseIcon sx={{ color: "white" }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </AppBar>
                            <Divider />
                        </Grid>
                    </Grid>
                    <Grid container p={2}>
                        <Grid item sm={12}>
                            <TextField
                                label="Hostname"
                                value={newHostname}
                                sx={{ width: "100%" }}
                                onChange={(e) => {
                                    const valid = isValidDomain(e.target.value);
                                    setHostnameValid(valid);
                                    setNewHostname(e.target.value)
                                }}
                                error={!hostnameValid && newHostname !== ""}
                                helperText={!hostnameValid && newHostname !== "" ? "Invalid domain name. Do not include http or https." : ""}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="save new hostname"
                                                onClick={() => {
                                                    if (hostnameValid) {
                                                        setHostnames(newHostname);
                                                        setNewHostname("");
                                                    }
                                                }}
                                                color={newHostname !== "" ? "success" : undefined}
                                                disabled={newHostname === "" || !hostnameValid}
                                                edge="end"
                                            >
                                                <Tooltip title="Save new Hostname">
                                                    <AddCircleIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
}

const HostnameChip = (props: ChipProps): React.JSX.Element => {
    return (
        <>
            <Chip {...props} />
            <input name="hostnames" type="hidden" value={`${props.label}`} />
        </>
    )
}

export const getHostnameChips = ({
    id,
    hostnames,
    deleteCallback,
    canonicalHostname
}: { hostnames: string[], deleteCallback: (hostname: string) => void, id: string, canonicalHostname: string }): React.JSX.Element[] => {
    return hostnames.map(h => h === `${id}.${PLATFORM_INGRESS_PATTERN}` || h === canonicalHostname
        ? (<HostnameChip
            sx={{ mr: 1 }}
            key={h}
            label={h}
            variant="outlined" />)
        : (<HostnameChip
            sx={{ mr: 1 }}
            key={h}
            label={h}
            onDelete={() => deleteCallback(h)}
            deleteIcon={<DeleteIcon />}
            variant="outlined" />)
    )
}

export const SiteForm = (props: { isNew: boolean }): React.JSX.Element => {
    const data = useLoaderData() as LoaderData<typeof site>;
    const [name, setSiteName] = useState<string>(data.name || "");
    const [id, setSiteId] = useState<string>(data.id);
    const [enableWww, setEnableWww] = useState<boolean>(data.enableWww);
    const [hostnames, setHostnames] = useState<string[]>(data.hostnames);
    const [service, setService] = useState<string>(data.service);
    const [hostnameModalOpen, setHostnameModalOpen] = useState<boolean>(false);
    const [canonicalHostname, setCanonicalHostname] = useState<string>(() => {
        if (hasRequiredCanonicalHostname(data.canonicalHostname)) {
            return String(data.canonicalHostname).trim();
        }
        // New sites start empty until a Site ID is entered; existing sites fall back to the platform host.
        return data.id ? `${data.id}.${PLATFORM_INGRESS_PATTERN}` : "";
    });
    const [isProd, setIsProd] = useState<boolean>(data.isProd);
    const [brandSlug, setBrandSlug] = useState<string>(data.brandSlug);
    const [theme, setTheme] = useState<string>(normalizeSiteTheme(data.theme));
    const [createdDate, setCreatedDate] = useState<string | null>(data.createdDate);
    const [goLiveDate, setGoLiveDate] = useState<string | null>(data.goLiveDate);
    const authenticatedContext = useContext(AuthenticatedAppContext);
    const [decommissionModalOpen, setDecommissionModalOpen] = useState<boolean>(false);
    const userPerms = authenticatedContext?.userPerms;
    const [repairSiteModalOpen, setRepairSiteModalOpen] = useState<boolean>(false);
    const [cloneSiteModalOpen, setCloneSiteModalOpen] = useState<boolean>(false);
    const [cloneSiteId, setCloneSiteId] = useState<string>("");
    const [cloneInProgress, setCloneInProgress] = useState<boolean>(false);
    const [pipelineWatch, setPipelineWatch] = useState<{
        qid: string;
        kind: "provision" | "clone";
        status?: SitePipelineStatus;
    } | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [toastMessage, setToastMessage] = useState({
        open: false,
        message: "",
        severity: 'success' as 'success' | 'error' | 'info' | 'warning'
    });
    const navigate = useNavigate();
    useEffect(() => {
        setSiteId(`${data.id}`);
    }, [data.id])
    useEffect(() => {
        setCreatedDate(data.createdDate || null);
    }, [data.createdDate])
    useEffect(() => {
        setGoLiveDate(data.goLiveDate || null);
    }, [data.goLiveDate])
    useEffect(() => {
        setTheme(normalizeSiteTheme(data.theme));
    }, [data.theme])

    // Watch provision/clone pipelines started from create or clone flows.
    // Depend on the specific query values (not the searchParams object) so we do not
    // abort/restart the watcher on unrelated param identity churn.
    const pipelineQid = searchParams.get("pipeline");
    const pipelineKindParam = searchParams.get("pipelineKind");
    useEffect(() => {
        const kind =
            pipelineKindParam === "clone"
                ? "clone"
                : pipelineKindParam === "provision"
                    ? "provision"
                    : null;
        if (!pipelineQid || !kind || !data.id) {
            return;
        }

        const controller = new AbortController();
        setPipelineWatch({ qid: pipelineQid, kind });

        watchSitePipeline(data.id, pipelineQid, {
            signal: controller.signal,
            onUpdate: (status) => setPipelineWatch({ qid: pipelineQid, kind, status }),
        })
            .then(async (finalStatus) => {
                setPipelineWatch(null);
                setSearchParams({}, { replace: true });

                // Success is ONLY COMPLETED. ASYNC_COMPLETE is intermediate and must not toast.
                if (interpretPipelineStatus(finalStatus).kind === "success") {
                    const toast = pipelineOutcomeToast(kind, data.id, { kind: "success" });
                    setToastMessage({ open: true, ...toast });
                    return;
                }

                // Confirm whether rollback actually removed the site before saying so.
                let siteStillExists = false;
                try {
                    siteStillExists = await siteRecordExists(data.id);
                } catch {
                    siteStillExists = false;
                }
                const outcome = interpretPipelineStatus(finalStatus, { siteStillExists });
                if (outcome.kind !== "failure") {
                    return;
                }
                const toast = pipelineOutcomeToast(kind, data.id, outcome);
                setToastMessage({ open: true, ...toast });
                // Only leave the page when the site was actually destroyed.
                if (outcome.rolledBack) {
                    window.setTimeout(() => navigate("/sites"), 3500);
                }
            })
            .catch((err) => {
                if (err?.name === "AbortError") {
                    return;
                }
                setPipelineWatch(null);
                setToastMessage({
                    open: true,
                    message: err?.message || "Lost track of pipeline status.",
                    severity: "error",
                });
            });

        return () => controller.abort();
    }, [data.id, pipelineQid, pipelineKindParam, setSearchParams, navigate]);

    const hostnameChips = [...getHostnameChips({
        hostnames,
        id: id,
        deleteCallback: (hostname: string) => {
            const newHostnames = hostnames.filter(h => h !== hostname);
            setHostnames(newHostnames);
        },
        canonicalHostname: canonicalHostname,
    }), <Chip label="Add Hostname" onClick={() => setHostnameModalOpen(true)} />];

    const decommissionSite = async () => {
        if (!userPerms?.includes('deployment:admin')) {
            setDecommissionModalOpen(false);
            return;
        }
        const decommissionRes = await authenticatedRequest(
            `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${data.id}`,
            {
                method: "delete",
                body: JSON.stringify({}),
            }
        );

        if (!decommissionRes.ok)
            throw decommissionRes;

        return navigate('/sites');
    };

    const repairSite = async () => {
        if (!userPerms?.includes('deployment:admin')) {
            setRepairSiteModalOpen(false);
            return;
        }
        const repairRes = await authenticatedRequest(
            `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${data.id}/repair`,
            {
                method: "put",
                body: JSON.stringify({}),
            }
        );

        if (!repairRes.ok)
            throw repairRes;

        setToastMessage({
            open: true,
            message: `Your request has been received. The repair will be processed shortly.`,
            severity: 'success'
        });
        setRepairSiteModalOpen(false);
    }

    const cloneSiteIdValid = isCloneSiteIdValid(cloneSiteId, data.id);

    const cloneSite = async () => {
        if (!userPerms?.includes('deployment:admin')) {
            setCloneSiteModalOpen(false);
            return;
        }
        if (!cloneSiteIdValid) {
            setToastMessage({
                open: true,
                message: !isSiteIdWithinLimit(cloneSiteId)
                    ? SITE_ID_TOO_LONG_MESSAGE
                    : "Enter a unique site ID using letters, numbers, underscores, or hyphens.",
                severity: 'error'
            });
            return;
        }
        setCloneInProgress(true);
        try {
            const cloneRes = await authenticatedRequest(
                `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${data.id}/clone`,
                {
                    method: "post",
                    body: JSON.stringify({ id: cloneSiteId }),
                }
            );
            if (!cloneRes.ok) {
                const errText = await cloneRes.text();
                setToastMessage({
                    open: true,
                    message: errText || "Clone request failed.",
                    severity: 'error'
                });
                setCloneInProgress(false);
                return;
            }
            setCloneSiteModalOpen(false);
            setCloneSiteId("");
            setToastMessage({
                open: true,
                message: `Clone of ${data.id} queued as ${cloneSiteId}. This can take longer than a standard provision while the database and media files are imported.`,
                severity: 'success'
            });
            const cloneBody = await cloneRes.json();
            const qid = parseQueueId(cloneBody);
            if (qid) {
                navigate(`/sites/${cloneSiteId}?pipeline=${encodeURIComponent(qid)}&pipelineKind=clone`);
            } else {
                navigate(`/sites/${cloneSiteId}`);
            }
        } catch (e) {
            setToastMessage({
                open: true,
                message: "Clone request failed.",
                severity: 'error'
            });
            setCloneInProgress(false);
        }
    }

    return (
        <>
            {pipelineWatch && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {pipelineInProgressLabel(pipelineWatch.kind, pipelineWatch.status)}
                </Alert>
            )}
            <Form id="site-create-form" method="post">
                <input type="hidden" name="isNew" value={`${props.isNew}`} />
                <Grid container spacing={1}>
                    <Grid p={2} pr={1} container>
                        <Grid pr={2} item sm={6} md={7}>
                            <TextField
                                sx={{ width: "100%" }}
                                label="Site Title/Name"
                                name="name"
                                inputProps={{
                                    required: true,
                                    pattern: `[A-Z|a-z|0-9]{1,64}`
                                }}
                                onChange={(e) => {
                                    setSiteName(`${e.target.value}`);
                                }}
                                defaultValue={data.name || name}
                                helperText="Site names should short and useful. By default this is the homepage title. The maximum character amount is 64; special characters, numbers, and lower/uppercase letters are allowed."
                            />
                        </Grid>
                        <Grid item sm={6} md={5}>
                            <TextField
                                sx={{ width: "100%" }}
                                name="id"
                                label="Site ID"
                                required
                                inputProps={{
                                    required: true,
                                    pattern: `[a-z]{4,6}`,
                                    ...(props.isNew ? { maxLength: MAX_SITE_ID_LENGTH } : {}),
                                }}
                                onChange={(e) => {
                                    setSiteId(`${e.target.value}`);
                                    // Only when a site is brand new do we have to manually initialize these values.
                                    // Once a site is created and stored in DynamoDB, these values are automatically populated.
                                    if (props.isNew) {
                                        setCanonicalHostname(`${e.target.value}.${PLATFORM_INGRESS_PATTERN}`);
                                        setHostnames([`${e.target.value}.${PLATFORM_INGRESS_PATTERN}`]);
                                    }
                                }}
                                disabled={!props.isNew}
                                defaultValue={id || ""}
                                helperText={props.isNew ? `${siteIdCharacterCount(id)} — Enter a short (4-6 characters), unique value to use as an ID. No spaces or special characters. This ID determines the database, filesystem sub-directory, and all other site-specific values.` : "Site IDs are not editable."}
                            />
                        </Grid>
                    </Grid>
                    <Grid p={2} pr={1} container>
                        <FormControl fullWidth>
                            <InputLabel id="service-select-label">Active Service</InputLabel>
                            <Select
                                name="service"
                                labelId="service-select-label"
                                id="service-selector"
                                defaultValue={service}
                                label="Service"
                                onChange={(e) => setService(e.target.value)}
                            >
                                <MenuItem value={'blue'}>Blue Service</MenuItem>
                                <MenuItem value={'green'}>Green Service</MenuItem>
                            </Select>
                            <FormHelperText>
                                The service determines what underlying code a given site uses to run.
                                New features/features being QA-d for client approval are often on the active canary service.
                                The latest stable release of code is on the production service.
                                The value of this can be changed throughout the lifecycle of a website to facilitate easy canary testing/QA & release cycles platform-wide.
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid p={2} pr={1} container>
                        <FormControl fullWidth required error={!hasRequiredCanonicalHostname(canonicalHostname)}>
                            <InputLabel id="canonicalHostname-select-label">Canonical Hostname</InputLabel>
                            <Select
                                name="canonicalHostname"
                                labelId="canonicalHostname-select-label"
                                id="canonicalHostname-selector"
                                required
                                value={canonicalHostname}
                                label="Canonical Hostname"
                                displayEmpty
                                onChange={(e) => setCanonicalHostname(String(e.target.value))}
                            >
                                {!hasRequiredCanonicalHostname(canonicalHostname) && (
                                    <MenuItem value="" disabled>
                                        <em>Select a hostname</em>
                                    </MenuItem>
                                )}
                                {[
                                    ...(hasRequiredCanonicalHostname(canonicalHostname) &&
                                        !hostnames.includes(canonicalHostname)
                                        ? [canonicalHostname]
                                        : []),
                                    ...hostnames,
                                ].map((h) => (
                                    <MenuItem key={h} value={h}>
                                        {h}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                Required. The primary hostname for the site (non www-prefixed). Choose from the eligible hostnames below.
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid p={2} pr={1} container>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox name="isProd" checked={isProd} onChange={(e) => setIsProd(e.target.checked)} />}
                                label={isProd ? `Site is in Production` : `Site is currently in-implementation`}
                            />
                            <FormHelperText>
                                Choose if this site should be labeled as in-production. This setting does not directly change
                                anything but does help to know which sites are live vs in implementation.
                            </FormHelperText>
                        </FormGroup>
                    </Grid>
                    <Grid p={2} pr={1} container>
                        <Grid pr={2} item sm={6} md={7}>
                            <TextField
                                sx={{ width: "100%" }}
                                label="Created Date"
                                name="createdDate"
                                defaultValue={createdDate || ""}
                                disabled
                            />
                        </Grid>
                    </Grid>
                    <Grid p={2} pr={1} container>
                        <Grid pr={2} item sm={6} md={7}>
                            <TextField
                                sx={{ width: "100%" }}
                                label="Go Live Date"
                                name="goLiveDate"
                                defaultValue={goLiveDate || ""}
                                disabled
                            />
                        </Grid>
                    </Grid>
                    <Grid p={2} pr={1} container>
                        <FormControl fullWidth>
                            <InputLabel id="brandSlug-select-label">Site Brand</InputLabel>
                            <Select
                                name="brandSlug"
                                labelId="brandSlug-select-label"
                                id="brandSlug-selector"
                                defaultValue={brandSlug}
                                label="brandSlug"
                                onChange={(e) => setBrandSlug(e.target.value)}
                            >
                                <MenuItem value={'bluetent'}>Bluetent</MenuItem>
                                <MenuItem value={'q4l'}>Q4Launch</MenuItem>
                                <MenuItem value={'lmpm'}>LMPM</MenuItem>
                                <MenuItem value={'bizcor'}>BizCor</MenuItem>
                            </Select>
                            <FormHelperText>
                                Brand slug for the site (Bluetent, Q4L, LMPM, BizCor). Does not replace the required canonical hostname selection.
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid p={2} pr={1} container>
                        <FormControl fullWidth>
                            <InputLabel id="theme-select-label">Theme design</InputLabel>
                            <Select
                                name="theme"
                                labelId="theme-select-label"
                                id="theme-selector"
                                value={theme}
                                label="Theme design"
                                onChange={(e) => setTheme(e.target.value)}
                            >
                                {SITE_THEME_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                WordPress theme is always Astra Child. Choose a design variant: Default (Hub patterns) or Slick (slick.html layouts + Slick styles).
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid container p={2} pt={0}>
                        <Grid item sm={12}>
                            <Typography
                                component="h1"
                                variant="h6"
                                color="inherit"
                                noWrap
                                sx={{ flexGrow: 1 }}
                            >
                                Domains and Routing
                            </Typography>
                            <Divider />
                        </Grid>
                    </Grid>
                    <Grid container p={2} pr={1}>
                        <Grid item sm={12}>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox name="enableWww" checked={enableWww} onChange={(e) => setEnableWww(e.target.checked)} />}
                                    label="Redirect traffic to WWW"
                                />
                            </FormGroup>
                        </Grid>
                    </Grid>
                    <Grid container p={2} pt={0}>
                        <Grid item sm={12} pb={2}>
                            <Typography
                                component="h1"
                                variant="h6"
                                color="inherit"
                                noWrap
                                sx={{ flexGrow: 1 }}
                            >
                                Eligible Hostnames
                            </Typography>
                            <FormHelperText>
                                The eligible hostnames should include the implementation URL & and other hostnames you would like to resolve to this website.
                                If you have configured the brandSlug above then the implementation URL will be set, automatically, upon site creation.
                            </FormHelperText>
                            <Divider />
                        </Grid>
                        <Grid item sm={12}>
                            <Grid item sm={12}>
                                {hostnameChips.map(c => c)}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid container p={2} pt={0} spacing={1} alignItems="left">
                        <Grid item sm={12}>
                            <Button type="submit" color="primary" className="contrast" variant="contained">
                                {props.isNew ? `Create Website` : `Save Site Configuration`}
                            </Button>
                        </Grid>
                        <Grid item sm={12}>
                            <Button color="primary" className="contrast" variant="contained" onClick={async () => {
                                // Make site promotion manual.
                                const promoteRes = await authenticatedRequest(
                                    `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${id}/promote`,
                                    {
                                        method: "put",
                                        body: JSON.stringify({}),
                                    }
                                );
                                if (!promoteRes.ok) throw promoteRes;
                            }}>Promote Site</Button>
                        </Grid>
                        {userPerms?.includes('deployment:admin') && data.id && (<Grid item sm={12}>
                            <Button color="primary" className="contrast" variant="contained" onClick={() => {
                                setRepairSiteModalOpen(true);
                            }}>Repair Site</Button>
                        </Grid>)}
                        {userPerms?.includes('deployment:admin') && data.id && !props.isNew && (<Grid item sm={12}>
                            <Button color="primary" className="contrast" variant="contained" onClick={() => {
                                setCloneSiteId("");
                                setCloneSiteModalOpen(true);
                            }}>Clone Site</Button>
                            <FormHelperText>
                                Cloning imports the database and media files, then rewrites URLs for the new site ID. This usually takes longer than a standard provision.
                            </FormHelperText>
                        </Grid>)}
                        <Grid item sm={12}>
                            {userPerms?.includes('deployment:admin') && data.id && (
                                <Button color="primary" className="contrast" variant="contained" onClick={() => {
                                    setDecommissionModalOpen(true);
                                }}>Decommission / Delete</Button>
                            )}
                        </Grid>
                        <Grid item sm={12}>

                            <FormHelperText>
                                Promoting a site will persist any DNS changes to CloudFlare. If a given hostname does not yet exist in CloudFlare, it will be created.
                            </FormHelperText>
                        </Grid>

                    </Grid>

                </Grid>
            </Form>


            <AddHostnameModal
                open={hostnameModalOpen}
                setHostnames={(newHostname: string) => {
                    hostnames.push(newHostname);
                    setHostnames(hostnames);
                    setHostnameModalOpen(false)
                }}
                handleClose={() => setHostnameModalOpen(false)}
            />

            <DynamicModal
                open={decommissionModalOpen}
                handleClose={() => setDecommissionModalOpen(false)}
                title="Confirmation"
            >
                <p>Are you sure you want to decommission/delete <strong>{name}</strong>?</p>
                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button variant="contained" color="primary" onClick={decommissionSite}>
                        Accept
                    </Button>
                </Box>
            </DynamicModal>

            <DynamicModal
                open={repairSiteModalOpen}
                handleClose={() => setRepairSiteModalOpen(false)}
                title="Confirmation"
            >
                <p>Are you sure you want to repair <strong>{name}</strong>?</p>
                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button variant="contained" color="primary" onClick={repairSite}>
                        Accept
                    </Button>
                </Box>
            </DynamicModal>

            <DynamicModal
                open={cloneSiteModalOpen}
                handleClose={() => {
                    if (!cloneInProgress) {
                        setCloneSiteModalOpen(false);
                        setCloneSiteId("");
                    }
                }}
                title="Clone Site"
            >
                <p>Clone <strong>{name || data.id}</strong> under a new unique site ID. The clone imports the database and media, rewrites URLs, and provisions as a non-production site.</p>
                <p>This can take longer than a standard provision because the full database and media files are imported.</p>
                <TextField
                    sx={{ width: "100%", mt: 2 }}
                    label="New Site ID"
                    value={cloneSiteId}
                    onChange={(e) => setCloneSiteId(e.target.value.trim())}
                    inputProps={{
                        pattern: `[a-zA-Z0-9_-]+`,
                        required: true,
                        maxLength: MAX_SITE_ID_LENGTH,
                    }}
                    helperText={cloneSiteIdHelperText(cloneSiteId, data.id)}
                    error={cloneSiteId.length > 0 && !cloneSiteIdValid}
                />
                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={cloneSite}
                        disabled={!cloneSiteIdValid || cloneInProgress}
                    >
                        {cloneInProgress ? "Cloning…" : "Clone"}
                    </Button>
                </Box>
            </DynamicModal>

            <ToastMessage
                open={toastMessage.open}
                message={toastMessage.message}
                severity={toastMessage.severity}
                onClose={() =>
                    setToastMessage((s) => ({ ...s, open: false }))
                }
            />
        </>
    )
}

export const Site = (props: SiteProps): React.JSX.Element => {
    return (
        <div className="site">
            <React.Suspense fallback={<Loader />}>
                <h3>
                    {props.name} ({props.id})
                </h3>
                <div>
                    <h4>Hostname</h4>
                    {props.canonicalHostname}
                    <h4>Responds at</h4>
                    <div>
                        {props.hostnames.join(",")}
                    </div>
                </div>
                <div>
                    <h5>Running on Service: <Service service={props.service} /></h5>
                </div>
            </React.Suspense>
        </div>
    )
}
